/* Sales Ladder — compact layout. One page, no section rail: the Mode cards, then
   the mode's own setup (Fixed: ladders; Adaptable: stages, vaults and per-item
   stage; Full automation: strategy), then an "Advanced" disclosure with
   the sales strategy prompt, Negotiation and Alerts. Everything saves on change:
   the sales policy is debounced like Monetisation; the mode, strategy, toggles
   and per-item picks go out straight away. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account, FixedLadder, LadderStrategy } from '../../../../api/client';
import { useAutosave, type AutosaveResult } from '../../../../hooks/useAutosave';
import { POLICY_DEFAULTS, type SNegotiation, type SPolicy, type SStage } from '../../Content/salesB/model';
import { useSalesCatalogue } from '../../SalesLadder/useSalesCatalogue';
import { buildSalesLadderSetupDef, SALES_LADDER_SETUP_ID, type LadderBridge } from '../../SalesLadder/salesLadderSetup';
import { TutorialRunner, isTutorialSeen } from '../../../Tutorial';
import { AdvancedToggle, Btn, Card, ErrorLine, Icon, Loading, PageFrame, Row, SaveStatus, Seg } from '../kit';
import { patchNow, usePolicy } from '../usePolicy';
import { font, mergeStatus } from '../style';
import { C } from '../tokens';
import { ContentBlock, VaultsBlock, type StageOpt } from './AdaptableContent';
import { useSalesPool } from './useSalesPool';
import { LadderEditor, LadderList } from './FixedLadders';
import { PolicyAdvanced } from './PolicyAdvanced';
import { StagesSection } from './StagesSection';
import { MODES, bodyOfDraft, deriveMode, draftOf, modeFlags, money, stageKeys, stageNames, validateDraft, type LadderMode, type PolicyDraft } from './model';
import { useTracker } from './useTracker';

const SALES_LADDER_REGIME = 'talk_sext_and_sales_ladder';
const MODE_ICON: Record<LadderMode, string> = { fixed: 'lock', adaptable: 'sparkles', auto: 'zap' };
const IDLE: AutosaveResult = { status: 'idle', error: null, flush: () => {}, retry: () => {} };

const FALLBACK: PolicyDraft = draftOf({ ...POLICY_DEFAULTS, exists: false, updated_at: null, applied_to_vault_ids: [] });

export function SalesLadderPage({ account, onAccountUpdate }: { account: Account; onAccountUpdate: (a: Account) => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tl } = useTranslation('salesLadder');
  const id = account.account_id;
  const tracker = useTracker();

  /* Mode — saved on click. */
  const [mode, setModeState] = useState<LadderMode>(deriveMode(account));
  useEffect(() => { setModeState(deriveMode(account)); }, [account.is_auto_ladder_active, account.is_fixed_ladder_active]); // eslint-disable-line react-hooks/exhaustive-deps
  const setMode = (m: LadderMode) => {
    if (m === mode) return;
    setModeState(m);
    void tracker.track(() => api.updateAccount(id, modeFlags(m))).then(onAccountUpdate, () => {});
  };

  // Sales is managed entirely here, so make sure the account's sales-content flag is on.
  useEffect(() => {
    if (account.is_sales_content_allowed) return;
    let off = false;
    api.updateAccount(id, { is_sales_content_allowed: true }).then((a) => { if (!off) onAccountUpdate(a); }, () => {});
    return () => { off = true; };
  }, [id, account.is_sales_content_allowed, onAccountUpdate]);

  /* Sales policy: stages, one-vault, negotiation, alerts, prompts. */
  const policy = usePolicy<PolicyDraft>(
    () => api.getSalesPolicy(id).then(draftOf),
    (d) => api.putSalesPolicy(id, bodyOfDraft(d)).then(() => undefined),
    FALLBACK,
    id,
    undefined,
    (d) => validateDraft(d, t),
  );
  const s = policy.body.s;
  const setS = (p: Partial<SPolicy>) => policy.patch({ s: { ...s, ...p } });
  const setSNow = (p: Partial<SPolicy>) => patchNow(policy, { s: { ...s, ...p } });
  const setNego = (p: Partial<SNegotiation>) => setS({ negotiation: { ...s.negotiation, ...p } });
  const setStages = (stages: SStage[]) => setS({ stages });

  /* Full automation: strategy (saved on click) + account-level offer framing (debounced). */
  const [strategy, setStrategyState] = useState<LadderStrategy>('bundles');
  useEffect(() => {
    let off = false;
    api.getAutoLadderStrategy(id).then((r) => { if (!off) setStrategyState(r.strategy); }, () => {});
    return () => { off = true; };
  }, [id]);
  const setStrategy = (v: LadderStrategy) => {
    setStrategyState(v);
    void tracker.track(() => api.setAutoLadderStrategy(id, v)).catch(() => {});
  };
  const [framing, setFraming] = useState(account.offer_framing_default ?? '');
  const framingAuto = useAutosave<string>({
    value: framing,
    save: async (v) => { onAccountUpdate(await api.updateAccount(id, { offer_framing_default: v.trim() || null })); },
  });

  /* Fixed ladders. undefined = list; null = a new ladder; FixedLadder = editing it. */
  const cat = useSalesCatalogue(id);
  const [ladders, setLadders] = useState<FixedLadder[]>([]);
  const [laddersLoading, setLaddersLoading] = useState(true);
  const [laddersErr, setLaddersErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<FixedLadder | null | undefined>(undefined);
  const [editorAuto, setEditorAuto] = useState<AutosaveResult>(IDLE);
  const [editorReady, setEditorReady] = useState(false);
  useEffect(() => {
    let off = false;
    setLaddersLoading(true);
    api.listFixedLadders(id).then(
      (l) => { if (!off) setLadders(l); },
      (e) => { if (!off) setLaddersErr(e instanceof Error ? e.message : String(e)); },
    ).finally(() => { if (!off) setLaddersLoading(false); });
    return () => { off = true; };
  }, [id]);
  const onLadderSaved = useCallback((l: FixedLadder) => setLadders((ls) => (ls.some((x) => x.id === l.id) ? ls.map((x) => (x.id === l.id ? l : x)) : [...ls, l])), []);
  const closeEditor = () => { setEditing(undefined); setEditorAuto(IDLE); setEditorReady(false); };
  const newPriority = ladders.length ? Math.min(...ladders.map((l) => l.priority)) - 1 : 0;

  /* Adaptable: attached vaults + their items. */
  const pool = useSalesPool(id, mode === 'adaptable', tracker.track);
  const stageOpts: StageOpt[] = useMemo(() => {
    const keys = stageKeys(s.stages);
    const names = stageNames(s.stages, t);
    return s.stages.map((x, k) => ({ key: keys[k]!, name: names[k]!, sub: x.kind === 'sale' ? money(x.price) || t('sl.noPrice') : t('sl.free') }));
  }, [s.stages, t]);

  /* Disclosures. */
  const [adv, setAdv] = useState(false);
  const [stOpen, setStOpen] = useState(true);
  const [vOpen, setVOpen] = useState(true);
  const advShow = !(mode === 'adaptable' && !stOpen);

  /* Guided setup (Sofi). */
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (autoOpened || laddersLoading) return;
    setAutoOpened(true);
    if (!isTutorialSeen(SALES_LADDER_SETUP_ID) && ladders.length === 0) setTutorialOpen(true);
  }, [autoOpened, laddersLoading, ladders.length]);
  const bridge: LadderBridge = {
    isFixed: () => mode === 'fixed',
    chooseFixed: () => setMode('fixed'),
    gotoSection: (sec) => { if (sec !== 'ladders') { closeEditor(); setAdv(true); } },
    openNewLadder: () => { setMode('fixed'); setEditing(null); },
    showLadderList: () => closeEditor(),
    isEditorOpen: () => editing !== undefined,
    isEditorReady: () => editorReady,
    laddersCount: () => ladders.length,
    activateRegime: async () => {
      await tracker.track(() => api.updateAccount(id, { ai_base_regime: SALES_LADDER_REGIME, default_automation_regime: SALES_LADDER_REGIME, ...modeFlags(mode) })).then(onAccountUpdate, () => {});
    },
  };
  const setupDef = useMemo(() => buildSalesLadderSetupDef(tl), [tl]);

  const save = mergeStatus(policy.auto, tracker, framingAuto, editorAuto);
  const ready = policy.state === 'ready';

  const advanced = advShow && (
    <>
      <AdvancedToggle open={adv} onToggle={() => setAdv((a) => !a)} hint={mode === 'auto' ? t('sl.advHintAuto') : t('sl.advHint')} />
      {adv && (policy.state === 'loading' ? <Loading /> : policy.state === 'failed' ? <ErrorLine>{t('loadFailed')}</ErrorLine> : (
        <PolicyAdvanced s={s} set={setS} setNego={setNego} setNow={setSNow} framing={mode === 'auto' ? { value: framing, onChange: setFraming } : undefined} />
      ))}
    </>
  );

  return (
    <PageFrame
      headline={t('sl.title')}
      desc={t('sl.desc')}
      right={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SaveStatus status={save.status} error={save.error} onRetry={save.retry} idleLabel={ready} />
          <Btn kind="muted" icon="help-circle" onClick={() => setTutorialOpen(true)}>{t('sl.guide')}</Btn>
        </div>
      }
    >
      <div data-tutorial="sl-mode" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ font: font(600, 13), color: C.text }}>{t('sl.mode.title')}</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 8 }}>
          {MODES.map((m) => <ModeCard key={m} m={m} on={m === mode} onPick={() => setMode(m)} />)}
        </div>
      </div>

      {mode === 'auto' && (
        <>
          <Card>
            <Row label={t('sl.auto.strategy')} help={t(strategy === 'video_only' ? 'sl.auto.videoHelp' : 'sl.auto.bundlesHelp')}>
              <Seg value={strategy} onChange={setStrategy} options={[{ id: 'bundles', label: t('sl.auto.bundles') }, { id: 'video_only', label: t('sl.auto.video') }]} />
            </Row>
          </Card>
          {advanced}
        </>
      )}

      {mode === 'adaptable' && (
        <>
          {policy.state === 'loading' && <Loading />}
          {policy.state === 'failed' && <ErrorLine>{t('loadFailed')}</ErrorLine>}
          {ready && <StagesSection s={s} set={setStages} open={stOpen} onToggle={() => setStOpen((o) => !o)} />}
          {ready && advanced}
          <VaultsBlock accountId={id} pool={pool} open={vOpen} onToggle={() => setVOpen((o) => !o)} one={s.restrictToOneVault} onOne={(v) => setSNow({ restrictToOneVault: v })} />
          {ready && <ContentBlock accountId={id} pool={pool} stages={stageOpts} />}
        </>
      )}

      {mode === 'fixed' && (
        <>
          {laddersErr && <ErrorLine>{laddersErr}</ErrorLine>}
          {editing === undefined ? (
            <LadderList accountId={id} ladders={ladders} loading={laddersLoading} cat={cat} onEdit={setEditing} onLadders={setLadders} track={tracker.track} />
          ) : (
            <LadderEditor
              key={editing?.id ?? 'new'}
              accountId={id}
              ladder={editing}
              priority={editing?.priority ?? newPriority}
              cat={cat}
              seedSalePrice={tutorialOpen ? 9.99 : undefined}
              onValidity={setEditorReady}
              onSaved={onLadderSaved}
              onClose={closeEditor}
              onStatus={setEditorAuto}
            />
          )}
          {advanced}
        </>
      )}

      <TutorialRunner def={setupDef} ctx={bridge} open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    </PageFrame>
  );
}

function ModeCard({ m, on, onPick }: { m: LadderMode; on: boolean; onPick: () => void }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <button type="button" onClick={onPick} className={on ? undefined : 'cpt-hover-border'} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14, boxSizing: 'border-box', background: on ? 'color-mix(in srgb, var(--om-accent) 10%, transparent)' : C.card, border: `1px solid ${on ? 'color-mix(in srgb, var(--om-accent) 60%, transparent)' : C.border}`, borderRadius: 10, cursor: 'pointer', textAlign: 'left', transition: 'border-color .15s, background .15s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
        <span style={{ width: 32, height: 32, flex: 'none', borderRadius: 8, background: on ? 'color-mix(in srgb, var(--om-accent) 22%, transparent)' : C.hover, color: on ? C.purplePale : C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={MODE_ICON[m]} size={16} /></span>
        <span style={{ flex: 1, font: font(600, 14), color: on ? C.text : 'var(--om-body)' }}>{t(`sl.mode.${m}`)}</span>
        <span style={{ width: 18, height: 18, flex: 'none', boxSizing: 'border-box', borderRadius: 99, border: `1.5px solid ${on ? C.purple : C.faint}`, background: on ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{on && <Icon name="check" size={11} />}</span>
      </div>
      <span style={{ font: font(400, 12, 1.45), color: on ? 'var(--om-body)' : C.sub }}>{t(`sl.mode.${m}Desc`)}</span>
    </button>
  );
}
